/**
 * WP29 ingestion signal — a single regulatory-change event lifted out of a
 * WP29 bundle (or any equivalent upstream feed). One signal can affect
 * zero, one or many rules — the linkage is materialised in
 * `affectedRuleIds`.
 *
 * Plan §22 W4 / W7 prototype: schema is contract-first so the API
 * worker, the web UI and the (future) D1 candidate table all share the
 * same shape. Provenance triplet (sourceUrl + sha256 + chunkId) is
 * MANDATORY per CI red line #4 (evidence-triplet).
 *
 * Lifecycle:
 *   - `candidate`   — fresh from ingest, not reviewed
 *   - `under_review` — assigned to a human reviewer
 *   - `promoted`    — merged into canonical (Review DO writes only path)
 *   - `dismissed`   — reviewer rejected; remains in the audit trail
 */

import { z } from "zod";

export const SignalChangeKind = z.enum([
  "amendment",
  "implementing_act",
  "new_regulation",
  "guidance_update",
  "withdrawal",
  "delegated_act",
  "implementing_decision",
  // RFC-0004 Phase 1a: a detected hash-diff on an already-archived official source.
  "source_amendment",
  // RFC-0005 / ADR-0013: upstream-draft and market-voice change kinds for the
  // U·P-band intel spine (advisory, never canonical).
  "draft",
  "proposed",
  "market_voice",
]);
export type SignalChangeKind = z.infer<typeof SignalChangeKind>;

export const SignalLifecycleState = z.enum([
  "candidate",
  "under_review",
  "promoted",
  "dismissed",
]);
export type SignalLifecycleState = z.infer<typeof SignalLifecycleState>;

export const Wp29SignalSchema = z.object({
  id: z.string().min(1),
  bundleId: z.string().min(1),
  fetchedAt: z.string().min(1),
  instrument: z.string().min(1),
  article: z.string().optional(),
  changeKind: SignalChangeKind,
  effectiveDate: z.string().nullable(),
  title: z.string().min(1),
  summary: z.string().min(1),
  // Provenance triplet (CI red line #4)
  sourceUrl: z.string().url(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  chunkId: z.string().min(1),
  // Linkage
  affectedRuleIds: z.array(z.string()),
  // Lifecycle
  lifecycleState: SignalLifecycleState,
  // HITL trail (W7 Review DO populates the rest)
  candidateOpenedAt: z.string(),
  reviewerNotes: z.string().optional(),
});
export type Wp29Signal = z.infer<typeof Wp29SignalSchema>;

export const Wp29BundleSchema = z.object({
  bundleId: z.string().min(1),
  fetchedAt: z.string().min(1),
  sourceLabel: z.string().min(1),
  signals: z.array(Wp29SignalSchema),
});
export type Wp29Bundle = z.infer<typeof Wp29BundleSchema>;
